// Browser functions the game uses that some players' browsers lack (error reports of 2026-10-25, all an empty board).
// The build rewrites new syntax for old browsers (frontend/vite.config.ts) but adds no missing functions, so these
// wrap them, with a plain fallback:
// - Chrome < 84 (Chrome 81 on Android 4.4 / 10, Samsung Internet 13 = Chrome 83): no document.timeline, no
//   Animation.finished, no `new Animation()` / `new KeyframeEffect()` (Element.animate() is there);
// - Safari < 14 (Safari 13.1 on macOS 10.13): a MediaQueryList without addEventListener (only addListener).
// In every newer browser these are the same calls as before.

/** Real time in ms on the document timeline (the clock of the card transitions, also under a frozen page.clock in the
 *  tests); performance.now() where there is no document.timeline. */
export function timelineNow(): number {
  const t = (document as { timeline?: AnimationTimeline }).timeline?.currentTime;
  return t == null ? performance.now() : Number(t);
}

export interface Wait {
  finished: Promise<void>;
  cancel(): void;
}

let animationTimers: boolean | null = null;
/** Whether an empty `new Animation(new KeyframeEffect(...))` can be made and has its `finished` promise. */
function canTimeWithAnimations(): boolean {
  if (animationTimers === null) {
    try {
      const a = new Animation(new KeyframeEffect(null, null, 1));
      animationTimers = !!a.finished && typeof a.finished.then === 'function';
    } catch {
      animationTimers = false;
    }
  }
  return animationTimers;
}

/** A wait of `ms` on the document timeline (an empty animation, like the card transitions); a timer where an animation
 *  cannot be made. `finished` resolves after `ms` and rejects when cancelled, like Animation.finished. */
export function waitFor(ms: number): Wait {
  if (canTimeWithAnimations()) {
    const a = new Animation(new KeyframeEffect(null, null, ms));
    a.play();
    return { finished: a.finished.then(() => undefined), cancel: () => a.cancel() };
  }
  let timer = 0;
  let reject: (e: unknown) => void = () => undefined;
  const finished = new Promise<void>((res, rej) => {
    reject = rej;
    timer = window.setTimeout(res, ms);
  });
  finished.catch(() => undefined); // a cancel nobody waits for is no unhandled rejection
  return { finished, cancel: () => { clearTimeout(timer); reject(new DOMException('cancelled', 'AbortError')); } };
}

/** Resolves when the animation has finished or was cancelled (Animation.finished, or its finish / cancel events). */
export function animationDone(a: Animation): Promise<void> {
  if (a.finished && typeof a.finished.then === 'function') return a.finished.then(() => undefined, () => undefined);
  if (a.playState === 'finished' || a.playState === 'idle') return Promise.resolve();
  return new Promise<void>((res) => {
    a.addEventListener('finish', () => res(), { once: true });
    a.addEventListener('cancel', () => res(), { once: true });
  });
}

/** Calls `cb` when the media query's answer changes; returns the function that stops it. */
export function onMediaChange(mq: MediaQueryList, cb: () => void): () => void {
  if (typeof mq.addEventListener === 'function') {
    mq.addEventListener('change', cb);
    return () => mq.removeEventListener('change', cb);
  }
  mq.addListener(cb);
  return () => mq.removeListener(cb);
}
